import React from 'react'
import PropTypes from 'prop-types'
import {
  Box,
  Grid,
  Title,
  IconManager,
  coreIconPack,
  mediaControlsIconPack,
  Tabs,
  Layout,
  ExpandableContent,
  DescriptionList,
  CMSContent,
  useTheme,
} from '@deseretbook/react-ui'
import { useMediaQuery } from '@deseretbook/react-utils'
import { Logger } from '../../../../dbcom'
import { ReviewsSection } from '../../reviews'

import { Overview } from '../'
import { Options as VideoOptions, JWPlayerVideo } from './'

IconManager.register(coreIconPack, mediaControlsIconPack)

const logger = new Logger({ context: 'product/video/VideoTemplate' })

/**
 * The template used for Deseret Video
 *
 * @returns {function} Component
 */
export const Video = ({
  product = {},
  user = {},
  libraryUrl = {},
  video = {},
  trailer = {},
  physicalVariants = [],
  videoVariant = {}
}) => {
  const [videoUrl, setVideoUrl] = React.useState('')
  const [activeTab, setActiveTab] = React.useState(0)
  const [playerActive, setPlayerActive] = React.useState(false)
  const theme = useTheme()

  const isFree = Number(videoVariant.price) === 0
  const userStatus = window.__USER__ || {}

  /**
   * Applies background image styles
   *
   * @param {string} direction - Direction of gradient
   *
   * @returns {object} Background image styles
   */
  const backgroundImageStyles = (direction) => ({
    backgroundImage: `linear-gradient(${direction}, ${theme.palette.dark.main} 0%, transparent 50%), url(${video.poster})`,
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  })

  /**
   * Sets the playerActive state to true.
   * Used mainly for styling purposes and loading the JW Player
   *
   * @param {string} url - The new URL
   */
  const activatePlayer = (url) => {
    setVideoUrl(url)
    setPlayerActive(true)
  }

  /**
   * Sets the active tab for smaller views
   *
   * @param {number} tabIndex - Which tab to set as active
   */
  const updateActiveTab = (tabIndex) => {
    setActiveTab(tabIndex)
  }

  // Applies backdrop to cover full width of screen
  const isFullDesktop = useMediaQuery({ minWidth: 1120 })

  const isDesktopOrLarger = useMediaQuery({ minWidth: 768 })

  // For when the content reaches its tablet breakpoint
  const isTabletOrSmaller = useMediaQuery({ maxWidth: 767 })
  // For when the content reaches its mobile breakpoint
  const isMobileOrSmaller = useMediaQuery({ maxWidth: 639 })

  /**
   * Options component used for easy reuse
   *
   * @returns {function} React Component
   */
  const OptionsComponent = () => (
    <VideoOptions
      user={user}
      trailer={trailer}
      video={video}
      activatePlayer={activatePlayer}
      physicalVariants={physicalVariants}
      isDeseretVideoSubscriber={userStatus.video}
      isVipSubscriber={userStatus.vip}
      isFree={isFree}
      isDark={!playerActive && isFullDesktop}
    />
  )

  /**
   * Overview component used for easy reuse
   *
   * @returns {function} React Component
   */
  const OverviewComponent = () => (
    <Overview
      product={product}
      isDark={!playerActive && isFullDesktop}
      updateActiveTab={updateActiveTab}
    />
  )

  return (
    <Layout>
      <Layout.Content>
        {/* Video Player and Overview Section */}
        {/* Larger than tablet layout */}
        {isDesktopOrLarger && (
          <Layout.Section
            style={{
              position: 'relative',
              ...(isDesktopOrLarger &&
                !playerActive && { color: 'white', backgroundColor: theme.palette.dark.main }),
            }}
            isPaddinglessHorizontal={playerActive}
          >
            <Grid
              columns={playerActive || !isFullDesktop ? Grid.fr(1) : [30, Grid.fr(1)]}
              rows="auto"
              gap={1}
              alignItems="end"
              style={{ zIndex: 10, position: 'relative' }}
            >
              {/* Video Information and rating */}
              {!playerActive && isFullDesktop && (
                <Grid.Item>
                  <OverviewComponent />
                  <Box paddingTop={2} isInline>
                    <OptionsComponent />
                  </Box>
                </Grid.Item>
              )}

              {/* Video Player */}
              <Grid.Item>
                {playerActive ? (
                  <Box
                    maxWidth={70}
                    isFullwidth
                    isFullheight
                    maxHeight={40}
                    padding={1}
                    style={{ margin: '0 auto', backgroundColor: theme.palette.dark.main }}
                  >
                    <JWPlayerVideo
                      libraryUrl={libraryUrl}
                      videoUrl={videoUrl}
                      videoPoster={video.poster}
                      videoContainerId="jw-player-container"
                      isAutoplay
                    />
                  </Box>
                ) : (
                  <Box
                    isFullwidth
                    height="55vh"
                    maxHeight={40}
                    style={backgroundImageStyles(isFullDesktop ? 'to right' : 'to top')}
                  />
                )}
              </Grid.Item>
            </Grid>
            {isFullDesktop && (
              <div
                style={{
                  backgroundColor: theme.palette.dark.main,
                  position: 'absolute',
                  top: '-1.5rem',
                  left: '-60%',
                  width: '200vw',
                  height: playerActive ? '100%' : '65vh',
                  maxHeight: '44.5rem',
                  zIndex: 5,
                }}
              />
            )}
          </Layout.Section>
        )}

        {isDesktopOrLarger && (playerActive || !isFullDesktop) && (
          <Layout.Section>
            <Box isColumn alignItems="start">
              <OverviewComponent />
              <Box paddingTop={2} isInline>
                <OptionsComponent />
              </Box>
            </Box>
          </Layout.Section>
        )}

        {/* Tablet and smaller layout */}
        {isTabletOrSmaller && (
          <Layout.Section>
            <React.Fragment>
              <Box>
                <React.Fragment>
                  {playerActive ? (
                    <JWPlayerVideo
                      libraryUrl={libraryUrl}
                      videoUrl={videoUrl}
                      videoPoster={video.poster}
                      videoContainerId="jw-player-container"
                      isAutoplay
                    />
                  ) : (
                    <Box height={15} style={backgroundImageStyles('to top')}>
                      <Box isFullwidth isFullheight alignItems="center" justifyContent="center" />
                    </Box>
                  )}
                  <OverviewComponent />

                  <Box paddingVertical={1} isColumn={isTabletOrSmaller}>
                    <OptionsComponent />
                  </Box>
                  <Box paddingTop={1}>
                    <ExpandableContent collapsedHeight={10}>
                      <CMSContent
                        itsNotDangerousBecause="It is dangerous. Fix this ASAP!!!!!"
                        dangerouslySetInnerHTML={{
                          __html: product.description,
                        }}
                      />
                    </ExpandableContent>
                  </Box>
                </React.Fragment>
              </Box>
            </React.Fragment>
          </Layout.Section>
        )}

        {/* Details and Reviews Sections */}

        {/* Larger than tablet Section */}
        {isDesktopOrLarger && (
          <React.Fragment>
            <Layout.Section>
              <Title title="Details" size={2} />
              <DescriptionList>
                {product.productProperties.map((productProperty) => (
                  <React.Fragment key={productProperty.id}>
                    <DescriptionList.Term>
                      {productProperty.property.presentation}
                    </DescriptionList.Term>
                    <DescriptionList.Description>
                      {productProperty.value}
                    </DescriptionList.Description>
                  </React.Fragment>
                ))}
              </DescriptionList>
            </Layout.Section>

            <Layout.Section>
              <Title title="Reviews" size={2} />
              <ReviewsSection product={product} />
            </Layout.Section>
          </React.Fragment>
        )}

        {/* Mobile Tabs Section */}
        {isMobileOrSmaller && (
          <Layout.Section>
            <Tabs activeTab={activeTab}>
              <Tabs.TabList tabType="default" isFullwidth={isMobileOrSmaller}>
                <Tabs.Tab>Details</Tabs.Tab>
                <Tabs.Tab>Reviews</Tabs.Tab>
              </Tabs.TabList>
              <Tabs.Panel>
                <DescriptionList>
                  {product.productProperties.map((productProperty) => (
                    <React.Fragment key={productProperty.id}>
                      <DescriptionList.Term>
                        {productProperty.property.presentation}
                      </DescriptionList.Term>
                      <DescriptionList.Description>
                        {productProperty.value}
                      </DescriptionList.Description>
                    </React.Fragment>
                  ))}
                </DescriptionList>
              </Tabs.Panel>
              <Tabs.Panel>
                <ReviewsSection product={product} />
              </Tabs.Panel>
            </Tabs>
          </Layout.Section>
        )}
      </Layout.Content>
    </Layout>
  )
}

Video.propTypes = {
  user: PropTypes.shape({
    subscriptions: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  product: PropTypes.shape({
    name: PropTypes.string,
    description: PropTypes.string,
    id: PropTypes.number,
    reviewsCount: PropTypes.number,
    avgRating: PropTypes.string,
    productProperties: PropTypes.arrayOf(PropTypes.object),
    price: PropTypes.string,
  }),
  libraryUrl: PropTypes.string,
  video: PropTypes.PropTypes.shape({
    url: PropTypes.string,
    poster: PropTypes.string,
  }),
  trailer: PropTypes.shape({
    url: PropTypes.string,
    poster: PropTypes.string,
  }),
  physicalVariants: PropTypes.arrayOf(PropTypes.object),
  videoVariant: PropTypes.arrayOf(PropTypes.object)
}
